import axios from 'axios';
import API from "../config/API";

// AI Concierge — public chat + salon-side admin.
// House convention: token rides the `accessToken` header (authenticateBearer
// reads req.get('accessToken')); Authorization carries the gateway Basic pair.
function headers() {
    let accessToken = localStorage.getItem('accessToken');
    let h = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA=="
    };
    if (accessToken) h.accessToken = `${accessToken}`;
    return h;
}

// Public, unauthenticated: is the assistant live for this salon? The widget
// renders nothing unless this says yes.
function status(salonId) {
    return axios.get(`${API.CONCIERGE_STATUS}?salonId=${encodeURIComponent(salonId)}`, { headers: headers() });
}

// A conversation read back: POST, so the session token rides in the body and
// never in a URL. Both the id and the token are needed.
function history(salonId, session) {
    const { sessionId, sessionToken } = session || {};
    return axios.post(API.CONCIERGE_HISTORY, { salonId, sessionId, sessionToken }, { headers: headers() });
}

function chat(params) {
    // Public endpoint; accessToken is OPTIONAL — with it the session is
    // identity-verified and the assistant can book directly.
    return axios.post(API.CONCIERGE_CHAT, params, { headers: headers() });
}

function getSettings() {
    return axios.get(API.CONCIERGE_SETTINGS, { headers: headers() });
}

function saveSettings(params) {
    return axios.post(API.CONCIERGE_SETTINGS, params, { headers: headers() });
}

function getFaqs() {
    return axios.get(API.CONCIERGE_FAQ, { headers: headers() });
}

function saveFaq(params) {
    return axios.post(API.CONCIERGE_FAQ, params, { headers: headers() });
}

function deleteFaq(faqId) {
    return axios.delete(`${API.CONCIERGE_FAQ}/${faqId}`, { headers: headers() });
}

function getTranscripts(page) {
    return axios.get(`${API.CONCIERGE_TRANSCRIPTS}?page=${page || 0}`, { headers: headers() });
}

function getMessages() {
    return axios.get(API.CONCIERGE_MESSAGES, { headers: headers() });
}

// The salon's own phone number. Beu issues it — it is never typed, because it
// is the routing key for every inbound call.
function getNumberOptions(params) {
    return axios.get(API.CONCIERGE_VOICE_NUMBER_OPTIONS, { headers: headers(), params });
}

function provisionNumber(params) {
    return axios.post(API.CONCIERGE_VOICE_NUMBER, params || {}, { headers: headers() });
}

function releaseNumber() {
    return axios.delete(API.CONCIERGE_VOICE_NUMBER, { headers: headers() });
}

function getVoices() {
    return axios.get(API.CONCIERGE_VOICES, { headers: headers() });
}

// Bailey's inbox: every conversation, one of them, a person replying in
// Bailey's place (which takes the chat over), and handing it back.
function conversations(params) {
    return axios.get(API.CONCIERGE_CONVERSATIONS, { headers: headers(), params });
}
function conversation(id) {
    return axios.get(`${API.CONCIERGE_CONVERSATIONS}/${encodeURIComponent(id)}`, { headers: headers() });
}
function replyTo(id, text) {
    return axios.post(`${API.CONCIERGE_CONVERSATIONS}/${encodeURIComponent(id)}/reply`, { text }, { headers: headers() });
}
// A voice greeting clients as this salon's receptionist, as an mp3 Blob.
// Errors come back as JSON (statusCode 0), so a JSON body is thrown.
async function voiceSample(voiceId) {
    const res = await axios.get(`${API.CONCIERGE_VOICES}/${encodeURIComponent(voiceId)}/sample`, { headers: headers(), responseType: 'blob' });
    const blob = res.data;
    if (!blob || /json/.test(blob.type || '')) {
        let msg = 'Couldn’t play that voice just now.';
        try { const j = JSON.parse(await blob.text()); msg = (j.error && j.error.responseMessage) || msg; } catch (e) { /* keep */ }
        throw new Error(msg);
    }
    return blob;
}

function setTakeover(id, active) {
    return axios.post(`${API.CONCIERGE_CONVERSATIONS}/${encodeURIComponent(id)}/takeover`, { active: !!active }, { headers: headers() });
}

// The anomaly brake: whether the receptionist is paused, and its alerts
// ({ paused, pausedAt, reason, alerts }). Resuming is the owner's alone.
function getAlerts() {
    return axios.get(API.CONCIERGE_ALERTS, { headers: headers() });
}
function resume() {
    return axios.post(API.CONCIERGE_RESUME, {}, { headers: headers() });
}

export const conciergeService = {
    status,
    history,
    chat,
    getSettings,
    saveSettings,
    getFaqs,
    saveFaq,
    deleteFaq,
    getTranscripts,
    getMessages,
    getNumberOptions,
    provisionNumber,
    releaseNumber,
    getVoices,
    voiceSample,
    conversations,
    conversation,
    replyTo,
    setTakeover,
    getAlerts,
    resume
};
